import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Mail, Send, CheckCircle2, AlertTriangle, CalendarClock, BellRing, Save, Footprints } from "lucide-react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DigestCard } from "@/components/admin/DigestCard";
import { SiteSettingsCard } from "@/components/admin/SiteSettingsCard";

const errDetail = (e, f) => { const d = e.response?.data?.detail; return typeof d === "string" ? d : f; };

const EmailSettingsCard = () => {
  const [s, setS] = useState(null);
  const [form, setForm] = useState({ sender_email: "", sender_name: "", app_base_url: "" });
  const [testTo, setTestTo] = useState("");
  const [busy, setBusy] = useState(false);
  const [testing, setTesting] = useState(false);

  const load = () => api.get("/admin/settings").then((r) => {
    setS(r.data);
    setForm({ sender_email: r.data.sender_email === r.data.env_sender ? "" : r.data.sender_email, sender_name: r.data.sender_name || "", app_base_url: r.data.app_base_url || window.location.origin });
  }).catch(() => {});
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true);
    try { await api.put("/admin/settings", form); toast.success("Email settings saved"); await load(); }
    catch (e) { toast.error(errDetail(e, "Could not save settings")); }
    finally { setBusy(false); }
  };
  const test = async () => {
    setTesting(true);
    try {
      const r = await api.post("/admin/settings/test-email", { to: testTo.trim() });
      if (r.data.ok) toast.success(`Test email delivered to ${r.data.to} from ${r.data.from}`);
      else toast.error(`Delivery failed: ${r.data.error}`, { duration: 12000 });
      await load();
    } catch (e) { toast.error(errDetail(e, "Test failed")); }
    finally { setTesting(false); }
  };

  if (!s) return null;
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6" data-testid="email-settings-card">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-5">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center"><Mail className="w-6 h-6 text-indigo-600" /></div>
          <div>
            <h2 className="font-heading font-bold text-lg text-slate-900">Email sender</h2>
            <p className="font-body text-sm text-slate-500">All invites, reports, alerts and digests are sent from this address.</p>
          </div>
        </div>
        <span className={`font-heading font-bold text-xs px-3 py-1.5 rounded-full ${s.sandbox ? "bg-amber-100 text-amber-800" : s.pending_verification ? "bg-amber-100 text-amber-800" : "bg-emerald-100 text-emerald-700"}`} data-testid="sender-mode-badge">
          {s.sandbox ? "Sandbox — only your own inbox receives mail" : s.pending_verification ? `Pending verification · ${s.sender_email} (sandbox fallback active)` : `Custom domain · ${s.sender_email}`}
        </span>
      </div>
      {(s.sandbox || s.pending_verification) && (
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-4 font-body text-sm text-amber-900 mb-5" data-testid="sandbox-help">
          <p className="font-semibold flex items-center gap-1.5"><AlertTriangle className="w-4 h-4" /> {s.pending_verification ? `Resend hasn't verified the sender domain yet — emails fall back to ${s.env_sender} automatically.` : `Using Resend's sandbox sender (${s.env_sender}).`}</p>
          <p className="mt-1">Your DNS records for <span className="font-mono">notifications.motleymentors.com</span> are already live. In Resend → <b>Domains</b> → notifications.motleymentors.com, click <b>Verify DNS Records</b>. Once it shows <b>Verified</b>, hit "Send test" below — no code changes needed, emails switch to your domain automatically.</p>
        </div>
      )}
      <div className="grid sm:grid-cols-3 gap-4">
        <div>
          <Label className="font-heading font-bold text-slate-800">Sender email</Label>
          <Input value={form.sender_email} onChange={set("sender_email")} placeholder="hello@motleymentors.com" className="mt-1.5 rounded-xl" data-testid="sender-email-input" />
          <p className="font-body text-[11px] text-slate-400 mt-1">Leave blank to use the sandbox sender.</p>
        </div>
        <div>
          <Label className="font-heading font-bold text-slate-800">Sender name</Label>
          <Input value={form.sender_name} onChange={set("sender_name")} placeholder="Motley Mentors" className="mt-1.5 rounded-xl" data-testid="sender-name-input" />
        </div>
        <div>
          <Label className="font-heading font-bold text-slate-800">App URL (used in scheduled emails)</Label>
          <Input value={form.app_base_url} onChange={set("app_base_url")} placeholder="https://motleymentors.com" className="mt-1.5 rounded-xl" data-testid="app-url-input" />
        </div>
      </div>
      <div className="flex flex-wrap items-center gap-3 mt-5">
        <Button onClick={save} disabled={busy} className="rounded-xl font-heading font-bold" data-testid="settings-save-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />} Save settings
        </Button>
        <div className="flex items-center gap-2 ml-auto">
          <Input value={testTo} onChange={(e) => setTestTo(e.target.value)} placeholder="Send test to (defaults to you)" className="rounded-xl w-64" data-testid="test-email-to-input" />
          <Button variant="outline" onClick={test} disabled={testing} className="rounded-xl font-heading font-bold" data-testid="test-email-btn">
            {testing ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send test
          </Button>
        </div>
      </div>
      {s.last_test && (
        <p className={`font-body text-xs mt-3 flex items-center gap-1.5 ${s.last_test.ok ? "text-emerald-700" : "text-rose-700"}`} data-testid="last-test-result">
          {s.last_test.ok ? <CheckCircle2 className="w-3.5 h-3.5" /> : <AlertTriangle className="w-3.5 h-3.5" />}
          Last test {new Date(s.last_test.at).toLocaleString()} → {s.last_test.to} from {s.last_test.from}: {s.last_test.ok ? "delivered" : s.last_test.error}
        </p>
      )}
    </div>
  );
};

const WeeklyCard = () => {
  const [last, setLast] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/weekly-summary/preview").then((r) => setLast(r.data.last)).catch(() => {});
  useEffect(() => { load(); }, []);
  const send = async () => {
    if (!window.confirm("Email every sub-user their weekly summary now?")) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/weekly-summary/send", { base_url: window.location.origin });
      toast.success(`Weekly summaries: ${r.data.sent} sent, ${r.data.skipped} skipped (no codes)${r.data.failed ? `, ${r.data.failed} failed` : ""}`);
      if (r.data.failed) toast.warning(r.data.errors[0], { duration: 9000 });
      load();
    } catch (e) { toast.error("Could not send summaries"); }
    finally { setBusy(false); }
  };
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-wrap items-center gap-6" data-testid="weekly-card">
      <div className="w-12 h-12 rounded-xl bg-emerald-50 flex items-center justify-center shrink-0"><CalendarClock className="w-6 h-6 text-emerald-600" /></div>
      <div className="flex-1 min-w-[220px]">
        <h2 className="font-heading font-bold text-lg text-slate-900">Weekly sub-user summary</h2>
        <p className="font-body text-sm text-slate-500 mt-0.5">Every Monday at 8am IST each sub-user with codes gets a note of who finished, which codes are still unused and their credits left.
          {last?.sent_at ? ` Last sent ${new Date(last.sent_at).toLocaleString()} (${last.trigger}).` : " Not sent yet."}</p>
      </div>
      <Button variant="outline" onClick={send} disabled={busy} className="rounded-xl font-heading font-bold" data-testid="weekly-send-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Send to all now
      </Button>
    </div>
  );
};

const RemindersCard = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/reminders/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/reminders/run", { base_url: window.location.origin });
      toast.success(`Reminders: ${r.data.due} due, ${r.data.sent} sent${r.data.failed ? `, ${r.data.failed} failed` : ""}`);
      if (r.data.failed) toast.warning(r.data.errors[0], { duration: 9000 });
      load();
    } catch (e) { toast.error("Could not run reminders"); }
    finally { setBusy(false); }
  };
  if (!st) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-wrap items-center gap-6" data-testid="reminders-card">
      <div className="w-12 h-12 rounded-xl bg-amber-50 flex items-center justify-center shrink-0"><BellRing className="w-6 h-6 text-amber-600" /></div>
      <div className="flex-1 min-w-[220px]">
        <h2 className="font-heading font-bold text-lg text-slate-900">Code expiry reminders</h2>
        <p className="font-body text-sm text-slate-500 mt-0.5">Candidates with an email who haven't started get a nudge {st.lead_days} days before their code expires (checked hourly).</p>
        <p className="font-body text-xs text-slate-500 mt-2" data-testid="reminders-status"><span className="font-semibold text-slate-700">{st.due_now}</span> due now · {st.reminded_total} reminded so far{st.last_run?.last_run ? ` · last run ${new Date(st.last_run.last_run).toLocaleString()}` : ""}</p>
      </div>
      <Button variant="outline" onClick={run} disabled={busy} className="rounded-xl font-heading font-bold" data-testid="reminders-run-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Run now
      </Button>
    </div>
  );
};

const NudgesCard = () => {
  const [st, setSt] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => api.get("/admin/nudges/status").then((r) => setSt(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);
  const run = async () => {
    setBusy(true);
    try {
      const r = await api.post("/admin/nudges/run", { base_url: window.location.origin });
      toast.success(`Nudges: ${r.data.due} due, ${r.data.sent} sent${r.data.failed ? `, ${r.data.failed} failed` : ""}`);
      if (r.data.failed) toast.warning(r.data.errors[0], { duration: 9000 });
      load();
    } catch (e) { toast.error("Could not run nudges"); }
    finally { setBusy(false); }
  };
  if (!st) return null;
  return (
    <div className="bg-white rounded-2xl border border-slate-200 p-6 flex flex-wrap items-center gap-6" data-testid="nudges-card">
      <div className="w-12 h-12 rounded-xl bg-indigo-50 flex items-center justify-center shrink-0"><Footprints className="w-6 h-6 text-indigo-600" /></div>
      <div className="flex-1 min-w-[220px]">
        <h2 className="font-heading font-bold text-lg text-slate-900">"You're halfway there" nudge</h2>
        <p className="font-body text-sm text-slate-500 mt-0.5">Candidates who started but haven't touched their assessment for {st.after_hours} hours get one friendly email with their progress and a resume link (checked hourly).</p>
        <p className="font-body text-xs text-slate-500 mt-2" data-testid="nudges-status"><span className="font-semibold text-slate-700">{st.in_progress_with_email}</span> in progress not yet nudged · {st.nudged_total} nudged so far{st.last_run?.last_run ? ` · last run ${new Date(st.last_run.last_run).toLocaleString()}` : ""}</p>
      </div>
      <Button variant="outline" onClick={run} disabled={busy} className="rounded-xl font-heading font-bold" data-testid="nudges-run-btn">
        {busy ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Send className="w-4 h-4 mr-1" />} Run now
      </Button>
    </div>
  );
};

export const SettingsPanel = () => (
  <div className="space-y-6" data-testid="settings-panel">
    <SiteSettingsCard />
    <EmailSettingsCard />
    <DigestCard />
    <WeeklyCard />
    <RemindersCard />
    <NudgesCard />
  </div>
);
